import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import QRCode from "qrcode";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { customFetch } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { extractApiErrorMessage } from "@/lib/api-error";
import { NomeDaMesa } from "@/lib/mesa";
import { useToast } from "@/hooks/use-toast";

type TableQr = { mesa: string; url: string };

// Na folha (A4, carta) os cartões saem em colunas, com corte pontilhado. Na impressora térmica (bobina de 58 ou 80 mm, a
// folha de impressão bem estreita) saem um embaixo do outro, com margem pequena (Pedro, 29/10/2026: em duas colunas na
// bobina o «Comanda 1» encavalava no «Comanda 2»), e o QR com 3 cm, como na impressora conectada (na largura toda do
// papel saíram grandes demais).
const IMPRESSAO = `
@media print {
  .no-print { display: none !important; }
  .qr-grid { gap: 0 !important; }
  .qr-card { break-inside: avoid; border-style: dashed !important; }
  @page { margin: 10mm; }
}
@media print and (max-width: 460px) {
  @page { margin: 3mm; }
  .qr-pagina { padding: 0 !important; max-width: none !important; }
  .qr-grid { display: block !important; }
  .qr-card { width: 100% !important; padding: 1mm 0 3mm !important; border-width: 0 0 1px 0 !important; border-radius: 0 !important; }
  .qr-card img, .qr-card > div.aspect-square { display: block !important; width: 30mm !important; max-width: 100% !important; margin: 1mm auto !important; }
  .qr-loja { font-size: 3mm !important; }
  .qr-nome { font-size: 7mm !important; }
  .qr-texto { font-size: 3mm !important; }
}
`;

/** QR codes das mesas, prontos para imprimir e colar (Configurações → Pedido na mesa). Com a comanda individual
 *  («1 QR code para cada cliente») cada cartão é uma comanda, entregue ao cliente (Pedro, 29/10/2026). */
export default function TableQrCodes() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["table-qrcodes"],
    queryFn: () => customFetch<{ storeName: string; porCliente?: boolean; tables: TableQr[] }>("/api/settings/table-qrcodes"),
  });
  const [images, setImages] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!data) return;
    let vivo = true;
    Promise.all(
      data.tables.map(async (t) => [t.mesa, await QRCode.toDataURL(t.url, { width: 480, margin: 1, errorCorrectionLevel: "M" })] as const),
    ).then((pares) => {
      if (vivo) setImages(Object.fromEntries(pares));
    });
    return () => {
      vivo = false;
    };
  }, [data]);

  const prontos = !!data && Object.keys(images).length === data.tables.length;
  const porCliente = data?.porCliente === true;
  // Impressora conectada na conta (Pedro, 29/10/2026): os QR codes saem nela, um embaixo do outro, com o corte entre eles.
  const { data: impressora } = useQuery({
    queryKey: ["printer-status"],
    queryFn: () => customFetch<{ printerConnectedAt: string | null }>("/api/print-queue/printer-status").catch(() => null),
  });
  const { toast } = useToast();
  const [enviando, setEnviando] = useState(false);
  const imprimirNaImpressora = async () => {
    setEnviando(true);
    try {
      const r = await customFetch<{ quantidade: number }>("/api/settings/table-qrcodes/imprimir", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      toast({ title: `${r.quantidade} QR codes enviados para a impressora.` });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível mandar para a impressora."), variant: "destructive" });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="qr-pagina mx-auto max-w-5xl px-4 py-6 sm:p-8">
      <style>{IMPRESSAO}</style>
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display">{porCliente ? "QR codes das comandas" : "QR codes das mesas"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {porCliente
              ? "Imprima e recorte. Cada cliente recebe uma comanda, que fica livre quando você fecha a conta no PDV."
              : "Imprima, recorte e cole um em cada mesa."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link href="/configuracoes">
              <ArrowLeft className="w-4 h-4 mr-1" /> Configurações
            </Link>
          </Button>
          {impressora?.printerConnectedAt ? (
            <>
              <Button variant="outline" onClick={() => window.print()} disabled={!prontos} data-testid="table-qrcodes-print">
                Imprimir pelo navegador
              </Button>
              <Button onClick={() => { void imprimirNaImpressora(); }} disabled={!data || enviando} data-testid="table-qrcodes-impressora">
                {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                Imprimir na impressora
              </Button>
            </>
          ) : (
            <Button onClick={() => window.print()} disabled={!prontos} data-testid="table-qrcodes-print">
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
        </div>
      )}
      {error && <p className="text-sm text-destructive">{extractApiErrorMessage(error, "Não foi possível gerar os QR codes.")}</p>}

      {data && (
        <div className="qr-grid grid grid-cols-2 gap-4 sm:grid-cols-3">
          {data.tables.map((t) => (
            <div
              key={t.mesa}
              className="qr-card flex flex-col items-center rounded-xl border bg-white p-4 text-center text-black"
              data-testid={`table-qr-${t.mesa}`}
            >
              <div className="qr-loja text-xs font-medium uppercase tracking-wide text-neutral-500 truncate max-w-full">{data.storeName}</div>
              <div className="qr-nome text-3xl font-bold leading-tight">
                {NomeDaMesa(porCliente)} {t.mesa}
              </div>
              {images[t.mesa] ? (
                <img src={images[t.mesa]} alt={`QR code da ${porCliente ? "comanda" : "mesa"} ${t.mesa}`} className="my-2 aspect-square w-full max-w-[220px]" />
              ) : (
                <div className="my-2 flex aspect-square w-full max-w-[220px] items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
                </div>
              )}
              <div className="qr-texto text-sm font-medium">Aponte a câmera do celular e faça seu pedido</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
